import { useState } from 'react';
import { PiBellLight } from 'react-icons/pi';
import { useAuth0 } from '@auth0/auth0-react';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { CountBadge } from 'src/modules/shared/components/atoms/count-badge';
import { Text } from 'src/modules/shared/components/atoms/text';
import { Popover } from 'src/modules/shared/components/molecules/popovers/popover';
import { type ContainerPopoverPlacement } from 'src/modules/shared/types/popover.type';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { useNotificationsUnreadCount } from '../../hooks/api/notifications';
import { NotificationInbox } from './notification-inbox';

interface NotificationsPopoverProps {
  /** Additional styling for the notification trigger. */
  buttonClassName?: string;
  /** Anchor the inbox to the available viewport space. */
  placement?: ContainerPopoverPlacement;
  /** Render a stable icon-and-label row in the desktop sidebar. */
  showLabel?: boolean;
  /** Hide the desktop label accessibly when the sidebar is compact. */
  isCollapsed?: boolean;
}

export const NotificationsPopover: React.FC<NotificationsPopoverProps> = ({
  buttonClassName = '',
  placement = 'bottom-end',
  showLabel = false,
  isCollapsed = false,
}) => {
  const { t } = useI18n();
  const { isAuthenticated, isLoading: loadingAuth } = useAuth0();
  const [isOpen, setIsOpen] = useState(false);

  const { data: unreadData } = useNotificationsUnreadCount(
    {},
    isAuthenticated && !loadingAuth
  );
  const unreadCount = unreadData?.unreadCount ?? 0;
  const notificationLabel = t('sidebar.notifications');

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const bellButton = (
    <Button
      appearance="ghost"
      variant="primary"
      size="sm"
      isIconOnly={!showLabel}
      data-sidebar-item={showLabel ? '' : undefined}
      className={`relative ${buttonClassName}`}
      title={notificationLabel}
      aria-label={notificationLabel}
    >
      <span data-sidebar-icon={showLabel ? '' : undefined}>
        <PiBellLight aria-hidden="true" className="h-5 w-5 text-primary-700" />
      </span>
      {showLabel && (
        <span data-sidebar-label aria-hidden={isCollapsed}>
          <Text as="span" size="small">
            {notificationLabel}
          </Text>
        </span>
      )}
      {unreadCount > 0 && (
        <CountBadge
          count={unreadCount}
          size="2xs"
          variant="primary"
          className={`absolute -top-1 ${showLabel ? 'left-6' : '-right-1'}`}
        />
      )}
    </Button>
  );

  return (
    <Popover
      handler={bellButton}
      open={isOpen}
      onOpenChange={handleOpenChange}
      placement={placement}
      contentClassName="w-[360px] max-w-[calc(100vw-2rem)]"
    >
      <div className="flex max-h-[70vh] flex-col">
        <div className="flex items-center justify-between border-b border-neutral-200 px-4 py-3">
          <Text size="small" weight="medium">
            {notificationLabel}
          </Text>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <NotificationInbox onClose={handleClose} />
        </div>
      </div>
    </Popover>
  );
};
